// Global Instructions Rule Applied!
//
// Templates tab — operator-defined Email Design Templates list.
//
// Sits next to Drafts + Sent log inside EmailCampaignsPage. Click a row
// (or the New button) to open the template detail form in a new app
// tab via the 'email-design-templates' module (registered in
// TabContentWrapper + useTabManager moduleMap).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Input, Popconfirm, Space, Table, Tag, Tooltip } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ImportOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import type { EmailDesignTemplateModel } from '../../../state_manager/data_models/email_design_template_model.ts'
import { useEmailDesignTemplates } from '../hooks/useEmailDesignTemplates.ts'
import { deleteEmailDesignTemplate } from '../services/emailDesignTemplateService.ts'

const TEMPLATES_MODULE = 'email-design-templates'

const formatDate = (iso?: string | null) => {
  if (!iso) return <span style={{ color: '#9ca3af' }}>—</span>
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const renderBodySource = (src: string) => {
  if (src === 'imported') {
    return (
      <Tag icon={<ImportOutlined />} color='purple'>
        Imported
      </Tag>
    )
  }
  return (
    <Tag icon={<FileTextOutlined />} color='blue'>
      Jodit
    </Tag>
  )
}

export default function TemplatesTab() {
  const {
    templatesData,
    templatesLoading,
    activeSorting,
    handleSetFilters,
    handleSetSorting,
    refetch
  } = useEmailDesignTemplates()
  const { openFormInTab } = useTabManagerContext()
  const location = useLocation()

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const currentSort = activeSorting?.[0]

  // Refetch when returning to the grid root after create/edit/cancel of a
  // template detail form. Same shape as DraftsTab.tsx.
  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = location.pathname
    if (
      (location.pathname === `/${TEMPLATES_MODULE}` || location.pathname === '/email') &&
      prev !== location.pathname
    ) {
      refetch()
    }
  }, [location.pathname, refetch])

  const openCreate = useCallback(() => {
    openFormInTab(TEMPLATES_MODULE, null)
  }, [openFormInTab])

  const openEdit = useCallback(
    (record: EmailDesignTemplateModel) => {
      openFormInTab(
        TEMPLATES_MODULE,
        record.email_design_templates__id,
        record.email_design_templates__name
      )
    },
    [openFormInTab]
  )

  const handleDelete = useCallback(
    async (record: EmailDesignTemplateModel) => {
      setDeletingId(record.email_design_templates__id)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await deleteEmailDesignTemplate(
          record.email_design_templates__id,
          token
        )
        if (!result.data || result.error) {
          showErrorToast(result.error || 'Failed to delete template')
          return
        }
        toast.success('Template deleted')
        await refetch()
      } finally {
        setDeletingId(null)
      }
    },
    [refetch]
  )

  const columns: ColumnsType<EmailDesignTemplateModel> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'email_design_templates__name',
        key: 'email_design_templates__name',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_design_templates__name'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (name: string, record: EmailDesignTemplateModel) => (
          <a onClick={() => openEdit(record)} className='font-medium'>
            {name || '(untitled)'}
          </a>
        )
      },
      {
        title: 'Subject default',
        dataIndex: 'email_design_templates__subject_default',
        key: 'email_design_templates__subject_default',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_design_templates__subject_default'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (subject: string) =>
          subject ? subject : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Source',
        dataIndex: 'email_design_templates__body_source',
        key: 'email_design_templates__body_source',
        width: 140,
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_design_templates__body_source'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (src: string) => renderBodySource(src)
      },
      {
        title: 'Size',
        dataIndex: 'email_design_templates__body_size_kb',
        key: 'email_design_templates__body_size_kb',
        width: 100,
        align: 'right',
        render: (kb: number) =>
          kb > 0 ? `${kb} KB` : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Last modified',
        dataIndex: 'email_design_templates__date_modified',
        key: 'email_design_templates__date_modified',
        width: 180,
        sorter: true,
        defaultSortOrder: 'descend',
        sortOrder:
          currentSort?.key === 'email_design_templates__date_modified'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (iso: string) => formatDate(iso)
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        align: 'center',
        render: (_: unknown, record: EmailDesignTemplateModel) => (
          <Space size='small'>
            <Tooltip title='Edit'>
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title='Delete this template?'
              description='Campaigns started from this template are unaffected (they hold their own body copy).'
              okText='Delete'
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title='Delete'>
                <Button
                  type='text'
                  size='small'
                  danger
                  loading={deletingId === record.email_design_templates__id}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    ],
    [currentSort, openEdit, handleDelete, deletingId]
  )

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: any) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter
      if (s && s.order && s.columnKey) {
        handleSetSorting([
          { key: String(s.columnKey), direction: s.order === 'descend' ? 'desc' : 'asc' }
        ])
      } else {
        handleSetSorting([
          { key: 'email_design_templates__date_modified', direction: 'desc' }
        ])
      }
    },
    [handleSetSorting]
  )

  const onSearch = useCallback(
    (value: string) => {
      const v = (value || '').trim()
      handleSetFilters(
        v ? [{ key: 'email_design_templates__name', value: v }] : []
      )
    },
    [handleSetFilters]
  )

  return (
    <div className='flex flex-col h-full'>
      <div className='flex items-center justify-between mb-3 gap-3 flex-wrap'>
        <Space>
          <Input.Search
            placeholder='Search templates by name...'
            allowClear
            onSearch={onSearch}
            style={{ width: 260 }}
          />
        </Space>
        <Space>
          <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
            New Template
          </Button>
        </Space>
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table<EmailDesignTemplateModel>
          className='email-design-templates-table'
          rowKey='email_design_templates__id'
          columns={columns}
          dataSource={templatesData}
          loading={templatesLoading}
          onChange={handleTableChange}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          size='middle'
        />
      </div>
    </div>
  )
}
